import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./Productdetailoverlay.css";
import { uiAlert } from "../../components/alerts/customAlert/uiAlert";
import { fetchAProductUrl } from "../../api/endpoints";
import RestockOverlay from "../stores/stockManagement/RestockNI";

/*
  ProductDetailOverlay
  ────────────────────
  Props:
    productID  — string  (fetches product from API)
    currency   — string e.g. "₦" or "kr"
    onClose    — () => void
    onRestock  — (product) => void  (optional)

  Usage:
    {selectedProductID && (
      <ProductDetailOverlay
        productID={selectedProductID}
        currency="₦"
        onClose={() => setSelectedProductID(null)}
        onRestock={(p) => openRestockForm(p)}
      />
    )}
*/

/* ─── Helpers ─── */
const fmt = (n) =>
  Number(n || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-NG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const timeAgo = (iso) => {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / 86400000);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor(diff / 60000);
  if (d > 0) return `${d} day${d !== 1 ? "s" : ""} ago`;
  if (h > 0) return `${h} hour${h !== 1 ? "s" : ""} ago`;
  return `${m} min${m !== 1 ? "s" : ""} ago`;
};

/* ─── Icons ─── */
const IconX = () => (
  <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <line x1='18' y1='6' x2='6' y2='18' />
    <line x1='6' y1='6' x2='18' y2='18' />
  </svg>
);
const IconPackage = () => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='#4A90E2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M16.5 9.4l-9-5.19M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z' />
    <polyline points='3.27 6.96 12 12.01 20.73 6.96' />
    <line x1='12' y1='22.08' x2='12' y2='12' />
  </svg>
);
const IconTag = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z' />
    <line x1='7' y1='7' x2='7.01' y2='7' />
  </svg>
);
const IconAlertTriangle = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#d97706' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z' />
    <line x1='12' y1='9' x2='12' y2='13' />
    <line x1='12' y1='17' x2='12.01' y2='17' />
  </svg>
);
const IconRefresh = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <polyline points='23 4 23 10 17 10' />
    <path d='M20.49 15a9 9 0 1 1-2.12-9.36L23 10' />
  </svg>
);
const IconWarehouse = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z' />
    <path d='M6 18h12' />
    <path d='M6 14h12' />
    <rect width='8' height='8' x='8' y='14' />
  </svg>
);
const IconCalendar = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <rect width='18' height='18' x='3' y='4' rx='2' ry='2' />
    <line x1='16' y1='2' x2='16' y2='6' />
    <line x1='8' y1='2' x2='8' y2='6' />
    <line x1='3' y1='10' x2='21' y2='10' />
  </svg>
);
const IconBox = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z' />
    <path d='m3.3 7 8.7 5 8.7-5' />
    <path d='M12 22V12' />
  </svg>
);
const IconHistory = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M3 3v5h5' />
    <path d='M3.05 13A9 9 0 1 0 6 5.3L3 8' />
    <path d='M12 7v5l4 2' />
  </svg>
);

/* ─── Section label ─── */
const SectionLabel = ({ children }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: 1,
      textTransform: "uppercase",
      color: "#6b7a99",
      marginBottom: 8,
      marginTop: 18,
    }}
  >
    {children}
  </div>
);

/* ─── Info row ─── */
const InfoRow = ({ icon, label, value, valueColor }) => {
  if (!value && value !== 0) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        borderBottom: "1px solid #f0f4fa",
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 7,
          background: "#f0f4fa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: "#1e3a5f",
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 9.5,
            fontWeight: 700,
            color: "#6b7a99",
            textTransform: "uppercase",
            letterSpacing: 0.4,
            marginBottom: 1,
          }}
        >
          {label}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: valueColor || "#1a2236" }}>{value}</div>
      </div>
    </div>
  );
};

/* ─── Image gallery ─── */
function ImageGallery({ images = [], name = "" }) {
  const [active, setActive] = useState(0);
  const validImages = images.filter(Boolean);
  if (validImages.length === 0) {
    return (
      <div
        style={{
          width: "100%",
          height: 200,
          background: "#f0f4fa",
          borderRadius: 12,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          color: "#6b7a99",
        }}
      >
        <IconPackage />
        <span style={{ fontSize: 12, fontWeight: 600 }}>No image</span>
      </div>
    );
  }
  return (
    <div>
      <div
        style={{
          width: "100%",
          height: 200,
          borderRadius: 12,
          overflow: "hidden",
          background: "#f0f4fa",
          marginBottom: 8,
        }}
      >
        <img
          src={validImages[active]}
          alt={name}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      </div>
      {validImages.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {validImages.map((img, i) => (
            <div
              key={i}
              onClick={() => setActive(i)}
              style={{
                width: 50,
                height: 50,
                borderRadius: 8,
                overflow: "hidden",
                border: i === active ? "2px solid #2563EB" : "2px solid #e2e8f2",
                cursor: "pointer",
                background: "#f0f4fa",
                flexShrink: 0,
              }}
            >
              <img
                src={img}
                alt={`${name} ${i + 1}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Stock bar ─── */
function StockBar({ quantity, stockLimit }) {
  if (!stockLimit || stockLimit === 0) return null;
  const pct = Math.min((quantity / stockLimit) * 100, 100);
  const color = pct <= 20 ? "#dc2626" : pct <= 50 ? "#d97706" : "#16a34a";
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7a99" }}>Stock level</span>
        <span style={{ fontSize: 11, fontWeight: 800, color }}>
          {quantity} / {stockLimit} units
        </span>
      </div>
      <div
        style={{
          width: "100%",
          background: "#e2e8f2",
          borderRadius: 99,
          height: 7,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            borderRadius: 99,
            background: color,
            width: `${pct}%`,
            transition: "width .8s ease",
          }}
        />
      </div>
    </div>
  );
}

/* ─── Sub-units table ─── */
function SubUnitsTable({ subUnits = [], currency }) {
  if (!subUnits || subUnits.length === 0) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f2",
        borderRadius: 11,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 1fr",
          background: "#1e3a5f",
          padding: "8px 12px",
        }}
      >
        {["Name", "Factor", "Stock", "Price"].map((h) => (
          <div
            key={h}
            style={{
              fontSize: 9,
              fontWeight: 800,
              color: "rgba(255,255,255,.6)",
              letterSpacing: 0.8,
              textTransform: "uppercase",
            }}
          >
            {h}
          </div>
        ))}
      </div>
      {subUnits.map((su, i) => (
        <div
          key={su.id || i}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr",
            padding: "9px 12px",
            borderBottom: i < subUnits.length - 1 ? "1px solid #f0f4fa" : "none",
            background: i % 2 === 0 ? "#fff" : "#f7f9fd",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "#1a2236" }}>{su.name || "—"}</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7a99" }}>×{su.conversionFactor || "—"}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: su.quantityOfSubUnitInStock <= 5 ? "#dc2626" : "#1a2236" }}>
            {su.quantityOfSubUnitInStock ?? "—"}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#2563EB" }}>
            {currency} {fmt(su.sellingPrice)}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Monthly sales chart ─── */
function MonthlySalesChart({ allSalesOffline = [], currency }) {
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const thisYear = new Date().getFullYear();

  // Handle both array and single-object shapes
  const offlineArr = Array.isArray(allSalesOffline) ? allSalesOffline : allSalesOffline ? [allSalesOffline] : [];

  const currentYear = offlineArr.find((y) => y.year === thisYear) || offlineArr[offlineArr.length - 1]; // fall back to latest year

  const rawMonths = currentYear?.salesByMonth || [];

  // Ensure all 12 months exist (fill gaps)
  const months = Array.from({ length: 12 }, (_, i) => {
    const found = rawMonths.find((m) => m.month === i);
    return found || { month: i, salesPrice: 0, quantity: 0 };
  });

  // Use quantity as primary metric; fall back to salesPrice
  const hasQtyData = months.some((m) => (m.quantity || 0) > 0);
  const hasRevData = months.some((m) => (m.salesPrice || 0) > 0);
  const useQty = hasQtyData;
  const getValue = (m) => (useQty ? m.quantity || 0 : m.salesPrice || 0);
  const maxVal = Math.max(...months.map(getValue), 1);

  const displayYear = currentYear?.year || thisYear;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f2",
        borderRadius: 11,
        padding: "12px 14px",
      }}
    >
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: "#6b7a99" }}>Sales this year ({displayYear})</div>
        <div style={{ display: "flex", gap: 10 }}>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: "#6b7a99" }}>{useQty ? "Qty sold" : "Revenue"}</span>
        </div>
      </div>

      {/* No data state */}
      {!hasQtyData && !hasRevData ? (
        <div style={{ textAlign: "center", padding: "16px 0", color: "#6b7a99", fontSize: 12, fontWeight: 600 }}>No sales recorded this year yet</div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 80, paddingBottom: 18 }}>
            {months.map((m) => {
              const val = getValue(m);
              const isCur = m.month === new Date().getMonth();
              const hasVal = val > 0;
              /* Use px heights — percentage heights don't work inside flex containers */
              const BAR_MAX = 56; // px — max bar height (80px total - 18px label - 6px gap)
              const barPx = hasVal ? Math.max(Math.round((val / maxVal) * BAR_MAX), 4) : 2;
              return (
                <div
                  key={m.month}
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 3,
                    height: "100%",
                  }}
                >
                  <div
                    title={hasVal ? `${MONTHS[m.month]}: ${useQty ? `${val} units` : `${currency} ${fmt(val)}`}` : `${MONTHS[m.month]}: No sales`}
                    style={{
                      width: "100%",
                      borderRadius: "3px 3px 0 0",
                      background: hasVal ? (isCur ? "#2563EB" : "#4A90E2") : "#e2e8f2",
                      height: barPx,
                      opacity: hasVal ? 1 : 0.5,
                      transition: "height .5s ease",
                      cursor: hasVal ? "pointer" : "default",
                      flexShrink: 0,
                    }}
                  />
                  <div
                    style={{
                      fontSize: 7.5,
                      fontWeight: 700,
                      color: isCur ? "#2563EB" : "#6b7a99",
                      flexShrink: 0,
                    }}
                  >
                    {MONTHS[m.month].slice(0, 1)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom legend: total units / revenue for the year */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 8, borderTop: "1px solid #f0f4fa" }}>
            <span style={{ fontSize: 10.5, fontWeight: 600, color: "#6b7a99" }}>Total {useQty ? "units sold" : "revenue"} this year</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: "#1e3a5f" }}>
              {useQty
                ? `${months.reduce((s, m) => s + (m.quantity || 0), 0)} units`
                : `${currency} ${fmt(months.reduce((s, m) => s + (m.salesPrice || 0), 0))}`}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   PIE CHART — Quantity Distribution
   Shows: In Store / Warehouse / Damaged
══════════════════════════════════════════ */
function QuantityPieChart({ inStore = 0, inWarehouse = 0, damaged = 0 }) {
  const canvasRef = useRef(null);

  const SLICES = [
    { label: "In Store", value: inStore, color: "#2563EB", dimColor: "#dbeafe" },
    { label: "Warehouse", value: inWarehouse, color: "#0d9488", dimColor: "#ccfbf1" },
    { label: "Damaged", value: damaged, color: "#dc2626", dimColor: "#fee2e2" },
  ];

  const total = inStore + inWarehouse + damaged;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    const cx = W / 2;
    const cy = H / 2;
    const R = Math.min(W, H) / 2 - 6;
    const INNER_R = R * 0.52; // donut hole

    ctx.clearRect(0, 0, W, H);

    if (total === 0) {
      // Empty state: grey ring
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.arc(cx, cy, INNER_R, 0, Math.PI * 2, true);
      ctx.fillStyle = "#e2e8f2";
      ctx.fill();

      ctx.fillStyle = "#6b7a99";
      ctx.font = "bold 11px Nunito, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("No data", cx, cy);
      return;
    }

    const GAP = 0.025; // gap between slices in radians
    let startAngle = -Math.PI / 2;

    SLICES.filter((s) => s.value > 0).forEach((slice) => {
      const sweep = (slice.value / total) * (Math.PI * 2) - GAP;

      // Outer arc
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R, startAngle, startAngle + sweep);
      ctx.closePath();
      ctx.fillStyle = slice.color;
      ctx.fill();

      startAngle += sweep + GAP;
    });

    // Cut out donut hole (white)
    ctx.beginPath();
    ctx.arc(cx, cy, INNER_R, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();

    // Centre text
    ctx.fillStyle = "#1e3a5f";
    ctx.font = "800 16px Nunito, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(total, cx, cy - 7);

    ctx.font = "700 8px Nunito, sans-serif";
    ctx.fillStyle = "#6b7a99";
    ctx.fillText("TOTAL", cx, cy + 8);
  }, [inStore, inWarehouse, damaged]);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f2",
        borderRadius: 11,
        padding: "14px 16px",
        display: "flex",
        alignItems: "center",
        gap: 18,
      }}
    >
      {/* Canvas */}
      <canvas ref={canvasRef} width={110} height={110} style={{ flexShrink: 0 }} />

      {/* Legend */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
        {SLICES.map((s) => {
          const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
          return (
            <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* Colour dot */}
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: s.value > 0 ? s.color : "#e2e8f2",
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "#1a2236" }}>{s.label}</div>
                <div style={{ fontSize: 10, fontWeight: 600, color: "#6b7a99" }}>
                  {s.value} unit{s.value !== 1 ? "s" : ""}
                </div>
              </div>
              {/* Pct badge */}
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: s.value > 0 ? s.color : "#6b7a99",
                  background: s.value > 0 ? s.dimColor : "#f0f4fa",
                  padding: "2px 7px",
                  borderRadius: 20,
                  flexShrink: 0,
                }}
              >
                {pct}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   PRODUCT HISTORY TAB
   Reads from product.history array:
   [{ date, activity: [string] }]
══════════════════════════════════════════ */
export function ProductHistoryMain({ history = [] }) {
  if (!history || history.length === 0) {
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f2",
          borderRadius: 11,
          padding: "32px 16px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1e3a5f", marginBottom: 5 }}>No history yet</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7a99" }}>Activity will appear here as this product is used.</div>
      </div>
    );
  }

  // Sort newest first
  const sorted = [...history].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div style={{ position: "relative" }}>
      {/* Vertical timeline line */}
      <div
        style={{
          position: "absolute",
          left: 17,
          top: 8,
          bottom: 8,
          width: 2,
          background: "#e2e8f2",
          borderRadius: 99,
          zIndex: 0,
        }}
      />

      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {sorted.map((entry, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              gap: 14,
              alignItems: "flex-start",
              position: "relative",
              zIndex: 1,
              paddingBottom: idx < sorted.length - 1 ? 16 : 0,
            }}
          >
            {/* Timeline dot */}
            <div
              style={{
                width: 36,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: "#2563EB",
                  border: "2px solid #fff",
                  boxShadow: "0 0 0 2px #dbeafe",
                  marginTop: 4,
                  flexShrink: 0,
                }}
              />
            </div>

            {/* Content card */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                background: "#fff",
                border: "1px solid #e2e8f2",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              {/* Date header */}
              <div
                style={{
                  padding: "8px 12px",
                  background: "#f7f9fd",
                  borderBottom: "1px solid #e2e8f2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "#1e3a5f",
                    letterSpacing: 0.3,
                  }}
                >
                  {fmtDate(entry.date)}
                </div>
                <div style={{ fontSize: 10, fontWeight: 600, color: "#6b7a99" }}>{timeAgo(entry.date)}</div>
              </div>

              {/* Activity list */}
              <div style={{ padding: "8px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                {(entry.activity || []).map((act, ai) => (
                  <div
                    key={ai}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 7,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#374151",
                      lineHeight: 1.5,
                    }}
                  >
                    <span
                      style={{
                        flexShrink: 0,
                        marginTop: 2,
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        background: "#6b7a99",
                        display: "inline-block",
                      }}
                    />
                    {act}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   LOADING SPINNER
══════════════════════════════════════════ */
function Spinner({ size = 40 }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(10,18,40,.5)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          border: `${size * 0.1}px solid rgba(255,255,255,.2)`,
          borderTopColor: "#fff",
          animation: "pdpSpin .7s linear infinite",
        }}
      />
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
export default function ProductDetailOverlay({ productID, currency = "₦", onClose }) {
  const overlayRef = useRef(null);
  const [tab, setTab] = useState("details"); // "details"|"stock"|"sales"|"history"
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openRestockWindow, setOpenRestockWindow] = useState(false);

  /* Escape key */
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  /* Fetch product + lock scroll */
  useEffect(() => {
    if (!productID) return;
    fetchProduct();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [productID]);

  const fetchProduct = async () => {
    setLoading(true);
    const url = fetchAProductUrl + productID;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to fetch product details");
      setProduct(data.product);
    } catch (error) {
      uiAlert(error?.message || "An error occurred while fetching product details", "error");
      console.error(error);
      setProduct({});
    } finally {
      setLoading(false);
    }
  };

  /* Derived flags */
  const isLowStock = product?.stockLimit ? product.quantity <= product.stockLimit * 0.2 : product?.quantity <= 5;

  const isExpired = product?.productExpiryDate ? new Date(product.productExpiryDate) < new Date() : false;

  const isNearExpiry = product?.productExpiryDate && !isExpired ? (new Date(product.productExpiryDate) - new Date()) / 86400000 <= 30 : false;

  const profit = (product?.unitPrice || 0) - (product?.costPrice || 0);
  const profitMargin = product?.costPrice ? ((profit / product.costPrice) * 100).toFixed(1) : null;

  if (loading) return createPortal(<Spinner size={40} />, document.body);
  if (!product) return null;

  return createPortal(
    <div
      className='pdp-overlay'
      ref={overlayRef}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose?.();
      }}
    >
      <div className='pdp-panel' role='dialog' aria-modal='true' aria-label={`${product.name} details`}>
        {/* ── HERO ── */}
        <div className='pdp-hero'>
          <button className='pdp-close' onClick={onClose} aria-label='Close'>
            <IconX />
          </button>

          {
            <button className='pdp-restock-btn' onClick={() => setOpenRestockWindow(true)}>
              <IconRefresh /> Restock
            </button>
          }

          <div className='pdp-hero-top'>
            <div className='pdp-thumb'>
              {product.productImage?.[0] ? (
                <img
                  src={product.productImage[0]}
                  alt={product.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <IconPackage />
              )}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div className='pdp-hero-name'>{product.name || "Unnamed Product"}</div>

              <div className='pdp-badges'>
                {product.productCategory && <span className='pdp-badge pdp-badge-cat'>{product.productCategory}</span>}
                {product.brand && <span className='pdp-badge pdp-badge-brand'>{product.brand}</span>}
                {product.productState && <span className='pdp-badge pdp-badge-state'>{product.productState}</span>}
                {isLowStock && <span className='pdp-badge pdp-badge-low'>⚠ Low Stock</span>}
                {isExpired && <span className='pdp-badge pdp-badge-expired'>Expired</span>}
                {isNearExpiry && <span className='pdp-badge pdp-badge-expiring'>Expiring Soon</span>}
                {product.onSales && <span className='pdp-badge pdp-badge-sale'>On Sale</span>}
              </div>

              <div className='pdp-price-row'>
                <span className='pdp-price'>
                  {currency} {fmt(product.unitPrice)}
                </span>
                {product.onSales && product.discountPrice && (
                  <span className='pdp-discount-price'>
                    {currency} {fmt(product.discountPrice)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className='pdp-stats-strip'>
            <div className='pdp-stat'>
              <div className='pdp-stat-lbl'>In Stock</div>
              <div className='pdp-stat-val' style={{ color: isLowStock ? "#fca5a5" : "#4ade80" }}>
                {product.quantity ?? "—"}
              </div>
            </div>
            <div className='pdp-stat'>
              <div className='pdp-stat-lbl'>Cost Price</div>
              <div className='pdp-stat-val'>
                {currency} {fmt(product.costPrice)}
              </div>
            </div>
            <div className='pdp-stat'>
              <div className='pdp-stat-lbl'>Sold Today</div>
              <div className='pdp-stat-val'>{product.soldAmountToday ?? 0}</div>
            </div>
            <div className='pdp-stat'>
              <div className='pdp-stat-lbl'>Sold All Time</div>
              <div className='pdp-stat-val'>{product.soldAmountAllTime ?? 0}</div>
            </div>
          </div>
        </div>

        {/* ── ALERTS ── */}
        {(isLowStock || isExpired || isNearExpiry) && (
          <div className='pdp-alerts'>
            {isLowStock && (
              <div className='pdp-alert pdp-alert-low'>
                <IconAlertTriangle />
                <span>
                  <strong>Low stock:</strong> Only {product.quantity} unit{product.quantity !== 1 ? "s" : ""} remaining
                  {product.stockLimit ? ` (limit: ${product.stockLimit})` : ""}.
                </span>
              </div>
            )}
            {isExpired && (
              <div className='pdp-alert pdp-alert-expired'>
                <IconAlertTriangle />
                <span>
                  <strong>Expired:</strong> This product expired on {fmtDate(product.productExpiryDate)}.
                </span>
              </div>
            )}
            {isNearExpiry && (
              <div className='pdp-alert pdp-alert-expiring'>
                <IconAlertTriangle />
                <span>
                  <strong>Expiring soon:</strong> Expires {fmtDate(product.productExpiryDate)}.
                </span>
              </div>
            )}
          </div>
        )}

        {/* ── TABS ── */}
        <div className='pdp-tabs'>
          {["details", "stock", "sales", "history"].map((t) => (
            <button key={t} className={`pdp-tab ${tab === t ? "pdp-tab-active" : ""}`} onClick={() => setTab(t)}>
              {t === "details" && "Details"}
              {t === "stock" && "Stock & Pricing"}
              {t === "sales" && "Sales"}
              {t === "history" && (
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <IconHistory />
                  History
                  {product.history?.length > 0 && (
                    <span
                      style={{
                        background: "#2563EB",
                        color: "#fff",
                        fontSize: 9.5,
                        fontWeight: 800,
                        padding: "1px 5px",
                        borderRadius: 20,
                      }}
                    >
                      {product.history.length}
                    </span>
                  )}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── BODY ── */}
        <div className='pdp-body'>
          {/* ── DETAILS ── */}
          {tab === "details" && (
            <div>
              <ImageGallery images={product.productImage} name={product.name} />

              {product.productDescription && (
                <>
                  <SectionLabel>Description</SectionLabel>
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #e2e8f2",
                      borderRadius: 11,
                      padding: "12px 14px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#374151",
                      lineHeight: 1.7,
                    }}
                  >
                    {product.productDescription}
                  </div>
                </>
              )}

              <SectionLabel>Product Info</SectionLabel>
              <div style={{ background: "#fff", border: "1px solid #e2e8f2", borderRadius: 11, overflow: "hidden" }}>
                <InfoRow icon={<IconTag />} label='Category' value={product.productCategory} />
                <InfoRow icon={<IconTag />} label='Brand' value={product.brand} />
                <InfoRow icon={<IconBox />} label='Unit' value={product.unit} />
                <InfoRow icon={<IconTag />} label='Condition' value={product.productState} />
                <InfoRow icon={<IconCalendar />} label='Created' value={fmtDate(product.creationDate)} />
                <InfoRow
                  icon={<IconCalendar />}
                  label='Expiry date'
                  value={fmtDate(product.productExpiryDate)}
                  valueColor={isExpired ? "#dc2626" : isNearExpiry ? "#d97706" : undefined}
                />
                {product.scannedID && <InfoRow icon={<IconTag />} label='Barcode / SKU' value={product.scannedID} />}
              </div>

              <SectionLabel>Store</SectionLabel>
              <div style={{ background: "#fff", border: "1px solid #e2e8f2", borderRadius: 11, overflow: "hidden" }}>
                <InfoRow icon={<IconWarehouse />} label='Store' value={product.storeName} />
                <InfoRow icon={<IconWarehouse />} label='Address' value={product.storeAddress} />
                {product.warehouse?.name && <InfoRow icon={<IconWarehouse />} label='Warehouse' value={product.warehouse.name} />}
              </div>

              {product.canBeDelivered && (
                <>
                  <SectionLabel>Delivery</SectionLabel>
                  <div style={{ background: "#fff", border: "1px solid #e2e8f2", borderRadius: 11, overflow: "hidden" }}>
                    <InfoRow icon={<IconBox />} label='Delivery fee' value={`${currency} ${fmt(product.deliveryFee)}`} />
                    <InfoRow icon={<IconCalendar />} label='Delivery time' value={`${product.deliveryTime} day${product.deliveryTime !== 1 ? "s" : ""}`} />
                  </div>
                </>
              )}

              {(product.sizeApplicability || product.colorApplicability) && (
                <>
                  <SectionLabel>Variants</SectionLabel>
                  <div style={{ background: "#fff", border: "1px solid #e2e8f2", borderRadius: 11, padding: "12px 14px" }}>
                    {product.sizeApplicability && product.selectedSizes?.length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7a99", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                          Sizes
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                          {product.selectedSizes.map((s) => (
                            <span
                              key={s}
                              style={{
                                background: "#f0f4fa",
                                border: "1px solid #e2e8f2",
                                borderRadius: 6,
                                padding: "3px 10px",
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#1a2236",
                              }}
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {product.colorApplicability && product.selectedColors?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7a99", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                          Colors
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {product.selectedColors.map((c) => (
                            <div key={c} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <div style={{ width: 16, height: 16, borderRadius: "50%", background: c, border: "2px solid #e2e8f2" }} />
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#1a2236" }}>{c}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── STOCK & PRICING ── */}
          {tab === "stock" && (
            <div>
              <SectionLabel>Stock Level</SectionLabel>
              <div style={{ background: "#fff", border: "1px solid #e2e8f2", borderRadius: 11, padding: "14px 16px" }}>
                <StockBar quantity={product.quantity} stockLimit={product.stockLimit} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
                  {[
                    { label: "Current stock", val: `${product.quantity ?? "—"} ${product.unit || "units"}`, color: isLowStock ? "#dc2626" : "#16a34a" },
                    { label: "Stock limit", val: `${product.stockLimit ?? "—"} ${product.unit || "units"}`, color: "#1a2236" },
                    {
                      label: "Damaged",
                      val: `${product.damagedAmount ?? 0} ${product.unit || "units"}`,
                      color: product.damagedAmount > 0 ? "#dc2626" : "#6b7a99",
                    },
                    { label: "In warehouse", val: `${product.amountInWarehouse ?? 0} ${product.unit || "units"}`, color: "#1a2236" },
                  ].map(({ label, val, color }) => (
                    <div key={label} style={{ background: "#f7f9fd", border: "1px solid #e2e8f2", borderRadius: 9, padding: "10px 12px" }}>
                      <div style={{ fontSize: 9.5, fontWeight: 700, color: "#6b7a99", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>
                        {label}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 800, color }}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── QUANTITY DISTRIBUTION PIE ── */}
              <SectionLabel>Quantity Distribution</SectionLabel>
              <QuantityPieChart inStore={product.quantity ?? 0} inWarehouse={product.amountInWarehouse ?? 0} damaged={product.damagedAmount ?? 0} />

              <SectionLabel>Pricing</SectionLabel>
              <div style={{ background: "#fff", border: "1px solid #e2e8f2", borderRadius: 11, overflow: "hidden" }}>
                {[
                  { label: "Unit price", val: `${currency} ${fmt(product.unitPrice)}`, color: "#1e3a5f" },
                  { label: "Cost price", val: `${currency} ${fmt(product.costPrice)}`, color: "#6b7a99" },
                  { label: "Profit", val: `${currency} ${fmt(profit)}`, color: profit >= 0 ? "#16a34a" : "#dc2626" },
                  ...(profitMargin ? [{ label: "Profit margin", val: `${profitMargin}%`, color: profit >= 0 ? "#16a34a" : "#dc2626" }] : []),
                  { label: "Total worth", val: `${currency} ${fmt(product.totalWorth)}`, color: "#2563EB" },
                  ...(product.onSales && product.discountPrice
                    ? [{ label: "Discount price", val: `${currency} ${fmt(product.discountPrice)}`, color: "#d97706" }]
                    : []),
                ].map(({ label, val, color }, i, arr) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 14px",
                      borderBottom: i < arr.length - 1 ? "1px solid #f0f4fa" : "none",
                    }}
                  >
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: "#6b7a99" }}>{label}</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color }}>{val}</span>
                  </div>
                ))}
              </div>

              {product.hasSubUnits && product.subUnits?.length > 0 && (
                <>
                  <SectionLabel>Sub-units</SectionLabel>
                  <SubUnitsTable subUnits={product.subUnits} currency={currency} />
                </>
              )}

              {product.reasonForDamage?.length > 0 && (
                <>
                  <SectionLabel>Damage Records</SectionLabel>
                  <div style={{ background: "#fff", border: "1px solid #e2e8f2", borderRadius: 11, overflow: "hidden" }}>
                    {product.reasonForDamage.map((r, i) => (
                      <div
                        key={i}
                        style={{
                          padding: "10px 14px",
                          borderBottom: i < product.reasonForDamage.length - 1 ? "1px solid #f0f4fa" : "none",
                        }}
                      >
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", marginBottom: 3 }}>{fmtDate(r.date)}</div>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: "#1a2236" }}>{r.reason}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── SALES ── */}
          {tab === "sales" && (
            <div>
              <SectionLabel>Today's Snapshot</SectionLabel>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 4 }}>
                {[
                  { label: "Sold today", val: product.soldAmountToday ?? 0, color: "#2563EB" },
                  { label: "Sold all time", val: product.soldAmountAllTime ?? 0, color: "#1e3a5f" },
                ].map(({ label, val, color }) => (
                  <div key={label} style={{ background: "#fff", border: "1px solid #e2e8f2", borderRadius: 11, padding: "13px 14px", textAlign: "center" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, color: "#6b7a99", textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 5 }}>
                      {label}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, color }}>{val}</div>
                  </div>
                ))}
              </div>

              <SectionLabel>Monthly Sales</SectionLabel>
              <MonthlySalesChart allSalesOffline={product.allSalesOffline} currency={currency} />

              {product.allSalesOnline?.length > 0 && (
                <>
                  <SectionLabel>Recent Online Sales</SectionLabel>
                  <div style={{ background: "#fff", border: "1px solid #e2e8f2", borderRadius: 11, overflow: "hidden" }}>
                    {product.allSalesOnline
                      .slice(-8)
                      .reverse()
                      .map((s, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "9px 14px",
                            borderBottom: i < Math.min(product.allSalesOnline.length, 8) - 1 ? "1px solid #f0f4fa" : "none",
                          }}
                        >
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#1a2236" }}>{fmtDate(s.date)}</div>
                            <div style={{ fontSize: 10.5, fontWeight: 600, color: "#6b7a99" }}>{timeAgo(s.date)}</div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 13, fontWeight: 800, color: "#16a34a" }}>
                              {currency} {fmt(s.salesPrice)}
                            </div>
                            <div style={{ fontSize: 10.5, fontWeight: 600, color: "#6b7a99" }}>
                              {s.quantity} unit{s.quantity !== 1 ? "s" : ""}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── HISTORY ── */}
          {tab === "history" && (
            <div>
              <SectionLabel>Product Activity Log</SectionLabel>
              <ProductHistoryMain history={product.history} />
            </div>
          )}
        </div>

        {/* ── FOOTER ── */}
        {
          <div className='pdp-footer'>
            <button className='pdp-restock-cta' onClick={() => setOpenRestockWindow(true)}>
              <IconRefresh /> Restock Product
            </button>
          </div>
        }

        {openRestockWindow && <RestockOverlay product={product} closeModal={setOpenRestockWindow} warehouse={false} updateSelectedProduct={setProduct} />}
      </div>
    </div>,
    document.body
  );
}
